import { useRef, useState } from 'react';
import heartIcon from './assets/heart-icon.png';
import orgWordmark from './assets/org-wordmark.png';
import { callApi } from './api';
import { saveSiteToken } from './authToken';

const DIGIT_COUNT = 4;

/** サイト入場コードの入力画面。Edge Function(gijiroku)のsite_loginで照合する。 */
export default function LoginScreen({ onEnter }) {
  const [digits, setDigits] = useState(Array(DIGIT_COUNT).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  const isComplete = digits.every((d) => d !== '');

  function setDigitAt(index, value) {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function handleChange(index, e) {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
    setDigitAt(index, value);
    if (error) setError('');
    if (value && index < DIGIT_COUNT - 1) inputRefs.current[index + 1]?.focus();
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(index, e) {
    const pasted = (e.clipboardData.getData('text').match(/[0-9]/g) || []).slice(0, DIGIT_COUNT);
    if (!pasted.length) return;
    e.preventDefault();
    setDigits((prev) => {
      const next = [...prev];
      pasted.forEach((d, i) => { next[i] = d; });
      return next;
    });
    inputRefs.current[Math.min(pasted.length, DIGIT_COUNT) - 1]?.focus();
  }

  function clearForRetry() {
    setDigits(Array(DIGIT_COUNT).fill(''));
    inputRefs.current[0]?.focus();
  }

  async function handleEnter(e) {
    e.currentTarget.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(.97)' }, { transform: 'scale(1)' }],
      { duration: 220, easing: 'ease-out' }
    );
    if (!isComplete || submitting) return;

    setSubmitting(true);
    setError('');
    try {
      const data = await callApi('site_login', { code: digits.join('') });
      saveSiteToken(data.token);
      onEnter();
    } catch (err) {
      setError(err.message);
      clearForRetry();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-wrap">
      <main className="pane">
        <img className="icon" src={heartIcon} width="94" height="96" alt="社会福祉法人 ザ・ハート・クラブ" />
        <img className="org-mark" src={orgWordmark} width="279" height="96" alt="社会福祉法人 ザ・ハート・クラブ" />
        <h1 className="wordmark reveal r3">
          記録ノート
          <svg viewBox="0 0 220 10" preserveAspectRatio="none">
            <path d="M2 6 C 40 2, 90 9, 130 5 S 190 2, 218 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        </h1>
        <p className="prompt reveal r4">認証番号を入力してください</p>
        <div className="pin reveal r4" role="group" aria-label={`認証番号 ${DIGIT_COUNT}桁`}>
          {digits.map((value, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={value}
              disabled={submitting}
              className={value ? 'filled' : ''}
              aria-label={`${i + 1}桁目`}
              onChange={(e) => handleChange(i, e)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
            />
          ))}
        </div>
        {error && <p className="pin-error" role="alert">{error}</p>}
        <button className="enter reveal r4" type="button" disabled={!isComplete || submitting} onClick={handleEnter}>
          {submitting ? '確認中…' : 'はじめる'}
        </button>
        <p className="hint reveal r5">コードがわからない場合は管理者にご確認ください</p>
      </main>
    </div>
  );
}
